import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '../services/api';
import {
  BarChart3, Users, Package, DollarSign, TrendingUp, Shield,
  Search, ChevronLeft, ChevronRight, Edit2, Trash2, Plus, X,
  UserCheck, UserX, Eye, ShieldCheck
} from 'lucide-react';

// ============================================================
// TAB: Dashboard Overview
// ============================================================
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data) return <p className="text-bastion-gray-500">Failed to load dashboard data.</p>;

  const { stats, revenueByMonth, topPackages, recentOrders } = data;

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Revenue"
          value={`$${(stats.totalRevenue / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
          sub={`$${(stats.monthlyRevenue / 100).toFixed(2)} this month`}
          icon={<DollarSign className="w-5 h-5" />}
          color="text-green-600 bg-green-50"
        />
        <StatCard
          label="Total Orders"
          value={stats.totalOrders}
          sub={`${stats.monthlyOrders} this month`}
          icon={<TrendingUp className="w-5 h-5" />}
          color="text-bastion-blue-500 bg-bastion-blue-50"
        />
        <StatCard
          label="Total Users"
          value={stats.totalUsers}
          sub={`${stats.activeUsers} new (30d)`}
          icon={<Users className="w-5 h-5" />}
          color="text-purple-600 bg-purple-50"
        />
        <StatCard
          label="Total Analyses"
          value={stats.totalAnalyses}
          sub="all time"
          icon={<BarChart3 className="w-5 h-5" />}
          color="text-bastion-orange-500 bg-bastion-orange-50"
        />
      </div>

      {/* Revenue by Month */}
      {revenueByMonth.length > 0 && (
        <div className="bg-white rounded-xl border border-bastion-gray-200 p-6">
          <h3 className="font-heading text-sm uppercase tracking-bastion-wide text-bastion-gray-800 mb-4">Revenue by Month</h3>
          <div className="space-y-2">
            {revenueByMonth.map(m => {
              const maxRevenue = Math.max(...revenueByMonth.map(r => parseInt(r.revenue)));
              const width = maxRevenue > 0 ? (parseInt(m.revenue) / maxRevenue * 100) : 0;
              return (
                <div key={m.month} className="flex items-center gap-3">
                  <span className="text-xs text-bastion-gray-500 w-20 shrink-0">{m.month}</span>
                  <div className="flex-1 bg-bastion-gray-100 rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-bastion-black h-full rounded-full flex items-center justify-end pr-2 transition-all"
                      style={{ width: `${Math.max(width, 8)}%` }}
                    >
                      <span className="text-white text-xs font-medium">
                        ${(parseInt(m.revenue) / 100).toFixed(0)}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs text-bastion-gray-500 w-16 text-right">{m.orders} orders</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Packages */}
        <div className="bg-white rounded-xl border border-bastion-gray-200 p-6">
          <h3 className="font-heading text-sm uppercase tracking-bastion-wide text-bastion-gray-800 mb-4">Top Packages</h3>
          <div className="space-y-3">
            {topPackages.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-bastion-gray-100 last:border-0">
                <span className="font-medium text-bastion-gray-800">{p.package_name}</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-bastion-black">{p.sales} sales</span>
                  <span className="text-xs text-bastion-gray-500 ml-2">${(parseInt(p.revenue) / 100).toFixed(2)}</span>
                </div>
              </div>
            ))}
            {topPackages.length === 0 && <p className="text-bastion-gray-400 text-sm">No sales yet</p>}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl border border-bastion-gray-200 p-6">
          <h3 className="font-heading text-sm uppercase tracking-bastion-wide text-bastion-gray-800 mb-4">Recent Orders</h3>
          <div className="space-y-3">
            {recentOrders.map(o => (
              <div key={o.id} className="flex items-center justify-between py-2 border-b border-bastion-gray-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-bastion-gray-800">{o.userEmail}</p>
                  <p className="text-xs text-bastion-gray-500">{o.package} - {o.credits} credits</p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-bastion-black">${o.amount.toFixed(2)}</span>
                  <p className="text-xs text-bastion-gray-400">{new Date(o.date).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
            {recentOrders.length === 0 && <p className="text-bastion-gray-400 text-sm">No orders yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TAB: Users Management
// ============================================================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 15;

  const loadUsers = () => {
    setLoading(true);
    api.get('/admin/users', { params: { page, limit, search } })
      .then(r => {
        setUsers(r.data.users);
        setTotal(r.data.total);
        setLoading(false);
      }).catch(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, [page, search]);

  const toggleActive = async (userId, currentActive) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { isActive: !currentActive });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user');
    }
  };

  const toggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (!confirm(`Change user role to ${newRole}?`)) return;
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update role');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bastion-gray-400" />
        <input
          type="text"
          placeholder="Search by email or name..."
          className="w-full pl-10 pr-4 py-2.5 border border-bastion-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bastion-black/10"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-bastion-gray-200 overflow-hidden">
        {loading ? <div className="p-8"><LoadingSpinner /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bastion-gray-50 border-b border-bastion-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">User</th>
                  <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Role</th>
                  <th className="text-center px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Credits</th>
                  <th className="text-center px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Analyses</th>
                  <th className="text-right px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Spent</th>
                  <th className="text-center px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Status</th>
                  <th className="text-right px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className={`border-b border-bastion-gray-100 hover:bg-bastion-gray-50 ${!u.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-bastion-gray-800">{u.name || 'N/A'}</p>
                      <p className="text-xs text-bastion-gray-500">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.role === 'admin' ? 'bg-bastion-red/10 text-bastion-red' : 'bg-bastion-gray-100 text-bastion-gray-600'
                      }`}>
                        {u.role === 'admin' && <ShieldCheck className="w-3 h-3" />}
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-medium">{u.credits}</td>
                    <td className="px-4 py-3 text-center">{u.analysesCount}</td>
                    <td className="px-4 py-3 text-right">${u.totalSpent.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleActive(u.id, u.isActive)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            u.isActive
                              ? 'hover:bg-red-50 text-bastion-gray-400 hover:text-red-600'
                              : 'hover:bg-green-50 text-bastion-gray-400 hover:text-green-600'
                          }`}
                          title={u.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {u.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => toggleRole(u.id, u.role)}
                          className="p-1.5 rounded-lg hover:bg-bastion-blue-50 text-bastion-gray-400 hover:text-bastion-blue-500 transition-colors"
                          title={`Make ${u.role === 'admin' ? 'user' : 'admin'}`}
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-bastion-gray-400">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-bastion-gray-500">{total} users total</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-bastion-gray-200 disabled:opacity-30 hover:bg-bastion-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-bastion-gray-600">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg border border-bastion-gray-200 disabled:opacity-30 hover:bg-bastion-gray-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: Packages Management
// ============================================================
function PackagesTab() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // package id or 'new'
  const [form, setForm] = useState({ name: '', slug: '', credits: '', priceCents: '', discountPercent: 0, sortOrder: 0 });

  const loadPackages = () => {
    api.get('/admin/packages').then(r => {
      setPackages(r.data.packages);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { loadPackages(); }, []);

  const startCreate = () => {
    setEditing('new');
    setForm({ name: '', slug: '', credits: '', priceCents: '', discountPercent: 0, sortOrder: packages.length + 1 });
  };

  const startEdit = (pkg) => {
    setEditing(pkg.id);
    setForm({
      name: pkg.name,
      slug: pkg.slug,
      credits: pkg.credits,
      priceCents: pkg.priceCents,
      discountPercent: pkg.discountPercent,
      sortOrder: pkg.sortOrder
    });
  };

  const cancelEdit = () => { setEditing(null); };

  const savePackage = async () => {
    try {
      if (editing === 'new') {
        await api.post('/admin/packages', {
          name: form.name,
          slug: form.slug,
          credits: parseInt(form.credits),
          priceCents: parseInt(form.priceCents),
          discountPercent: parseInt(form.discountPercent) || 0,
          sortOrder: parseInt(form.sortOrder) || 0
        });
      } else {
        await api.put(`/admin/packages/${editing}`, {
          name: form.name,
          credits: parseInt(form.credits),
          priceCents: parseInt(form.priceCents),
          discountPercent: parseInt(form.discountPercent) || 0,
          sortOrder: parseInt(form.sortOrder) || 0
        });
      }
      setEditing(null);
      loadPackages();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save package');
    }
  };

  const deletePackage = async (id) => {
    if (!confirm('Deactivate this package? It will no longer appear on the pricing page.')) return;
    try {
      await api.delete(`/admin/packages/${id}`);
      loadPackages();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete package');
    }
  };

  const toggleActive = async (pkg) => {
    try {
      await api.put(`/admin/packages/${pkg.id}`, { isActive: !pkg.isActive });
      loadPackages();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update package');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-bastion-gray-500">{packages.length} packages</p>
        <button
          onClick={startCreate}
          className="flex items-center gap-2 px-4 py-2 bg-bastion-black text-white rounded-lg text-sm font-medium hover:bg-bastion-primary-hover transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Package
        </button>
      </div>

      {/* Edit/Create Form */}
      {editing !== null && (
        <div className="bg-bastion-gray-50 rounded-xl border border-bastion-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading text-sm uppercase tracking-bastion-wide">
              {editing === 'new' ? 'New Package' : 'Edit Package'}
            </h3>
            <button onClick={cancelEdit} className="p-1 hover:bg-bastion-gray-200 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-bastion-gray-600 mb-1">Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-bastion-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bastion-black/10"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Premium Package"
              />
            </div>
            {editing === 'new' && (
              <div>
                <label className="block text-xs font-medium text-bastion-gray-600 mb-1">Slug (unique ID)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-bastion-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bastion-black/10"
                  value={form.slug}
                  onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="e.g. premium"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-bastion-gray-600 mb-1">Credits</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-bastion-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bastion-black/10"
                value={form.credits}
                onChange={e => setForm({ ...form, credits: e.target.value })}
                placeholder="10"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-bastion-gray-600 mb-1">Price (cents)</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-bastion-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bastion-black/10"
                value={form.priceCents}
                onChange={e => setForm({ ...form, priceCents: e.target.value })}
                placeholder="3899 = $38.99"
              />
              {form.priceCents && (
                <p className="text-xs text-bastion-gray-400 mt-1">${(parseInt(form.priceCents) / 100).toFixed(2)}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-bastion-gray-600 mb-1">Discount %</label>
              <input
                type="number"
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-bastion-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bastion-black/10"
                value={form.discountPercent}
                onChange={e => setForm({ ...form, discountPercent: e.target.value })}
              />
              {form.priceCents && form.discountPercent > 0 && (
                <p className="text-xs text-green-600 mt-1">
                  Effective: ${(parseInt(form.priceCents) * (1 - parseInt(form.discountPercent) / 100) / 100).toFixed(2)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-bastion-gray-600 mb-1">Sort Order</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-bastion-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bastion-black/10"
                value={form.sortOrder}
                onChange={e => setForm({ ...form, sortOrder: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={savePackage}
              className="px-4 py-2 bg-bastion-black text-white rounded-lg text-sm font-medium hover:bg-bastion-primary-hover transition-colors"
            >
              {editing === 'new' ? 'Create Package' : 'Save Changes'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-4 py-2 border border-bastion-gray-200 rounded-lg text-sm hover:bg-bastion-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Packages List */}
      <div className="bg-white rounded-xl border border-bastion-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bastion-gray-50 border-b border-bastion-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Package</th>
                <th className="text-center px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Credits</th>
                <th className="text-right px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Price</th>
                <th className="text-center px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Discount</th>
                <th className="text-right px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Effective</th>
                <th className="text-center px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Status</th>
                <th className="text-right px-4 py-3 font-heading text-xs uppercase tracking-bastion-wide text-bastion-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.map(pkg => (
                <tr key={pkg.id} className={`border-b border-bastion-gray-100 hover:bg-bastion-gray-50 ${!pkg.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-bastion-gray-800">{pkg.name}</p>
                    <p className="text-xs text-bastion-gray-400">{pkg.slug}</p>
                  </td>
                  <td className="px-4 py-3 text-center font-medium">{pkg.credits}</td>
                  <td className="px-4 py-3 text-right">${pkg.price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">
                    {pkg.discountPercent > 0 ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        {pkg.discountPercent}% off
                      </span>
                    ) : (
                      <span className="text-bastion-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold">
                    ${pkg.effectivePrice.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleActive(pkg)}
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                        pkg.isActive ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-red-50 text-red-700 hover:bg-red-100'
                      }`}
                    >
                      {pkg.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => startEdit(pkg)}
                        className="p-1.5 rounded-lg hover:bg-bastion-blue-50 text-bastion-gray-400 hover:text-bastion-blue-500 transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePackage(pkg.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-bastion-gray-400 hover:text-red-600 transition-colors"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Shared Components
// ============================================================
function StatCard({ label, value, sub, icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-bastion-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-heading uppercase tracking-bastion-wide text-bastion-gray-500">{label}</span>
        <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      </div>
      <p className="text-2xl font-bold text-bastion-black">{value}</p>
      <p className="text-xs text-bastion-gray-400 mt-1">{sub}</p>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bastion-black"></div>
    </div>
  );
}

// ============================================================
// Main Admin Dashboard Page
// ============================================================
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'packages', label: 'Packages', icon: Package },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bastion-black"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-bastion-offwhite">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading text-bastion-black uppercase tracking-bastion-wide">
            Admin Dashboard
          </h1>
          <p className="text-sm text-bastion-gray-500 mt-1">Manage your platform</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-white rounded-xl border border-bastion-gray-200 p-1 mb-6 w-fit">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-bastion-black text-white'
                    : 'text-bastion-gray-600 hover:bg-bastion-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'packages' && <PackagesTab />}
      </div>
    </div>
  );
}
